/**
 * HeatTensionAdjustDrawer.jsx
 *
 * Drawer tool for adjusting emotional tension and sexual heat in a chapter.
 * Uses Opus-class models only (Sonnet is not calibration-reliable for this).
 *
 * Flow:
 *  1. User picks chapter + sets tension/heat percentage deltas
 *  2. Clicks "Run" → background job starts (polled every 4s)
 *  3. When done: side-by-side diff of original vs rewritten
 *  4. Admin clicks "Apply" to commit — or discards
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Flame,
  Info,
  Loader2,
  Play,
  RotateCcw,
  Wind,
  Zap,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { heatTensionApi } from '@/lib/api';

const POLL_MS = 4000;

// Only Opus models should be used for this feature
const OPUS_MODELS = [
  { id: 'claude-opus-4-5',  label: 'Claude Opus 4.5',  note: 'Recommended' },
  { id: 'claude-opus-4-7',  label: 'Claude Opus 4.7',  note: 'Latest' },
];

// ── Percentage input ──────────────────────────────────────────────────────────
function DeltaInput({ label, icon: Icon, color, value, onChange, testId }) {
  const isIncrease = value > 0;
  const isDecrease = value < 0;
  return (
    <div className="space-y-2">
      <Label className={cn('text-[13px] font-semibold uppercase tracking-wider flex items-center gap-1.5', color)}>
        <Icon className="h-3.5 w-3.5" />
        {label}
      </Label>
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="h-8 w-8 rounded border border-border bg-muted hover:bg-muted/80 flex items-center justify-center font-bold text-base transition-colors"
          onClick={() => onChange(Math.max(-100, value - 10))}
          data-testid={`${testId}-dec`}
        >
          −
        </button>
        <div className="relative flex-1">
          <input
            type="number"
            min={-100}
            max={100}
            step={5}
            value={value}
            onChange={(e) => onChange(Math.max(-100, Math.min(100, Number(e.target.value))))}
            data-testid={testId}
            className={cn(
              'h-8 w-full rounded border bg-background text-center text-[13px] font-bold tabular-nums focus:outline-none focus:ring-1 focus:ring-ring',
              isIncrease ? 'border-emerald-300 text-emerald-700' :
              isDecrease ? 'border-rose-300 text-rose-700' :
              'border-border text-muted-foreground',
            )}
          />
          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[13px] text-muted-foreground">%</span>
        </div>
        <button
          type="button"
          className="h-8 w-8 rounded border border-border bg-muted hover:bg-muted/80 flex items-center justify-center font-bold text-base transition-colors"
          onClick={() => onChange(Math.min(100, value + 10))}
          data-testid={`${testId}-inc`}
        >
          +
        </button>
      </div>
      <p className={cn(
        'text-[13px] font-medium',
        isIncrease ? 'text-emerald-600' : isDecrease ? 'text-rose-600' : 'text-muted-foreground',
      )}>
        {value === 0 ? 'No change' : isIncrease ? `↑ Increase by ${value}%` : `↓ Decrease by ${Math.abs(value)}%`}
      </p>
    </div>
  );
}

// ── Simple word-level diff ────────────────────────────────────────────────────
function buildWordDiff(original, rewritten) {
  // Split into sentences for tractable diffs
  const origParas  = original.split('\n').filter(Boolean);
  const rewriteParas = rewritten.split('\n').filter(Boolean);
  return { origParas, rewriteParas };
}

// ── Side-by-side diff pane ───────────────────────────────────────────────────
function DiffPane({ original, rewritten }) {
  const { origParas, rewriteParas } = buildWordDiff(original, rewritten);
  const maxLen = Math.max(origParas.length, rewriteParas.length);

  const origWords  = original.trim().split(/\s+/).length;
  const rewriteWords = rewritten.trim().split(/\s+/).length;
  const wordDelta  = rewriteWords - origWords;

  return (
    <div className="space-y-2">
      {/* Stats bar */}
      <div className="flex items-center gap-3 text-[13px] text-muted-foreground px-1">
        <span>Original: <strong>{origWords.toLocaleString()} words</strong></span>
        <span>·</span>
        <span>Rewritten: <strong>{rewriteWords.toLocaleString()} words</strong></span>
        <span>·</span>
        <span className={cn('font-semibold', wordDelta > 0 ? 'text-emerald-600' : wordDelta < 0 ? 'text-rose-600' : 'text-muted-foreground')}>
          {wordDelta > 0 ? `+${wordDelta}` : wordDelta} words
        </span>
      </div>

      {/* Column headers */}
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-md border border-border overflow-hidden">
          <div className="px-3 py-1.5 bg-muted/30 border-b border-border text-[13px] font-semibold text-muted-foreground uppercase tracking-wider">
            Original
          </div>
          <ScrollArea className="h-[380px]">
            <div className="p-3 space-y-2">
              {origParas.map((p, i) => (
                <p
                  key={i}
                  className={cn(
                    'text-[14px] leading-relaxed',
                    (rewriteParas[i] !== undefined && rewriteParas[i] !== p)
                      ? 'bg-rose-50/60 rounded px-1 -mx-1'
                      : 'text-foreground',
                  )}
                >
                  {p}
                </p>
              ))}
            </div>
          </ScrollArea>
        </div>

        <div className="rounded-md border border-emerald-200 overflow-hidden">
          <div className="px-3 py-1.5 bg-emerald-50/40 border-b border-emerald-200 text-[13px] font-semibold text-emerald-700 uppercase tracking-wider">
            Rewritten
          </div>
          <ScrollArea className="h-[380px]">
            <div className="p-3 space-y-2">
              {rewriteParas.map((p, i) => (
                <p
                  key={i}
                  className={cn(
                    'text-[14px] leading-relaxed',
                    (origParas[i] !== undefined && origParas[i] !== p)
                      ? 'bg-emerald-50/70 rounded px-1 -mx-1'
                      : 'text-foreground',
                  )}
                >
                  {p}
                </p>
              ))}
            </div>
          </ScrollArea>
        </div>
      </div>

      <p className="text-[13px] text-muted-foreground px-1">
        Highlighted paragraphs differ from the original.
      </p>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export function HeatTensionAdjustDrawer({
  open,
  onClose,
  projectId,
  activeChapterId,
  chapters = [],
}) {
  // Form state
  const [chapterId,    setChapterId]   = useState(activeChapterId || '');
  const [tensionDelta, setTensionDelta] = useState(0);
  const [heatDelta,    setHeatDelta]   = useState(0);
  const [modelId,      setModelId]     = useState(OPUS_MODELS[0].id);

  // Job state
  const [job,          setJob]         = useState(null);
  const [running,      setRunning]     = useState(false);
  const [applying,     setApplying]    = useState(false);
  const pollRef = useRef(null);

  // Reset chapter when prop changes
  useEffect(() => {
    if (activeChapterId) setChapterId(activeChapterId);
  }, [activeChapterId]);

  const stopPoll = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };
  useEffect(() => () => stopPoll(), []);

  const startPoll = useCallback((jobId) => {
    stopPoll();
    pollRef.current = setInterval(async () => {
      try {
        const j = await heatTensionApi.pollJob(projectId, jobId);
        setJob(j);
        if (['done', 'error'].includes(j.status)) {
          stopPoll();
          setRunning(false);
          if (j.status === 'error') toast.error(`Adjustment failed: ${j.error}`);
          else toast.success('Rewrite ready — review the diff below.');
        }
      } catch { /* transient */ }
    }, POLL_MS);
  }, [projectId]);

  const handleRun = async () => {
    if (!chapterId) { toast.error('Select a chapter first.'); return; }
    if (Math.abs(tensionDelta) < 1 && Math.abs(heatDelta) < 1) {
      toast.error('Set at least one delta to a non-zero value.');
      return;
    }
    setRunning(true);
    setJob(null);
    try {
      const res = await heatTensionApi.startAdjust(projectId, {
        chapter_id:    chapterId,
        tension_delta: tensionDelta,
        heat_delta:    heatDelta,
        model_id:      modelId,
      });
      setJob({ job_id: res.job_id, status: 'queued' });
      startPoll(res.job_id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to start adjustment.');
      setRunning(false);
    }
  };

  const handleApply = async () => {
    if (!job) return;
    setApplying(true);
    try {
      await heatTensionApi.applyJob(projectId, job.job_id);
      toast.success('Rewrite applied to chapter.');
      setJob((j) => ({ ...j, applied: true }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Apply failed.');
    } finally {
      setApplying(false);
    }
  };

  const handleReset = () => {
    stopPoll();
    setJob(null);
    setRunning(false);
    setTensionDelta(0);
    setHeatDelta(0);
  };

  const selectedChapter = chapters.find((c) => c.id === chapterId);
  const isDone  = job?.status === 'done';
  const applied = job?.applied;

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) { stopPoll(); onClose(); } }}>
      <SheetContent
        side="right"
        className="flex flex-col gap-0 p-0 overflow-hidden"
        style={{ width: isDone ? '67vw' : '420px', maxWidth: '900px' }}
        data-testid="heat-tension-drawer"
      >
        <SheetHeader className="border-b border-border px-5 py-3 bg-panel-header/40 shrink-0">
          <SheetTitle className="flex items-center gap-2 text-[14px]">
            <Flame className="h-4 w-4 text-rose-500" />
            Heat & Tension Adjustment
          </SheetTitle>
        </SheetHeader>

        <ScrollArea className="flex-1">
          <div className="p-5 space-y-5">

            {/* Chapter selector */}
            <div className="space-y-1.5">
              <Label className="text-[13px] font-semibold uppercase tracking-wider text-muted-foreground">
                Chapter
              </Label>
              <select
                value={chapterId}
                onChange={(e) => setChapterId(e.target.value)}
                className="h-9 w-full rounded-md border border-border bg-background px-2.5 text-[14px] focus:outline-none focus:ring-1 focus:ring-ring"
                data-testid="ht-chapter-select"
              >
                <option value="">— Select chapter —</option>
                {chapters.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.number ? `Ch. ${c.number}: ` : ''}{c.title || c.name || `Chapter ${c.number || ''}`}
                  </option>
                ))}
              </select>
            </div>

            <Separator />

            {/* Delta inputs */}
            <div className="grid grid-cols-2 gap-5">
              <DeltaInput
                label="Emotional Tension"
                icon={Wind}
                color="text-sky-600"
                value={tensionDelta}
                onChange={setTensionDelta}
                testId="ht-tension-delta"
              />
              <DeltaInput
                label="Sexual Heat"
                icon={Flame}
                color="text-rose-500"
                value={heatDelta}
                onChange={setHeatDelta}
                testId="ht-heat-delta"
              />
            </div>

            <Separator />

            {/* Model selector */}
            <div className="space-y-1.5">
              <Label className="text-[13px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                <Zap className="h-3 w-3" />
                Model (Opus only)
              </Label>
              <select
                value={modelId}
                onChange={(e) => setModelId(e.target.value)}
                className="h-9 w-full rounded-md border border-border bg-background px-2.5 text-[14px] focus:outline-none focus:ring-1 focus:ring-ring"
                data-testid="ht-model-select"
              >
                {OPUS_MODELS.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.label} {m.note ? `— ${m.note}` : ''}
                  </option>
                ))}
              </select>
              <p className="flex items-center gap-1.5 text-[13px] text-muted-foreground">
                <Info className="h-3 w-3 text-sky-500 shrink-0" />
                Sonnet does not reliably follow calibration instructions. Opus 4.5+ is required.
              </p>
            </div>

            {/* Run / status */}
            {!isDone && !running && (
              <Button
                onClick={handleRun}
                disabled={!chapterId || (Math.abs(tensionDelta) < 1 && Math.abs(heatDelta) < 1)}
                className="w-full gap-2"
                data-testid="ht-run-btn"
              >
                <Play className="h-4 w-4" />
                Run Adjustment
              </Button>
            )}

            {running && (
              <div className="flex items-center gap-3 rounded-md border border-amber-200 bg-amber-50/60 p-3 text-[14px] text-amber-700">
                <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                <span>
                  {job?.status === 'queued' ? 'Queued — Opus is starting…' : 'Rewriting chapter with Opus…'}
                  <span className="block text-[13px] text-amber-600 mt-0.5">
                    This may take 1–3 minutes for longer chapters.
                  </span>
                </span>
              </div>
            )}

            {/* Diff view */}
            {isDone && job.original_text && job.rewritten_text && (
              <>
                <div className="flex items-center justify-between">
                  <p className="text-[14px] font-semibold text-foreground flex items-center gap-1.5">
                    <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                    Rewrite Ready — Review Changes
                  </p>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 text-[13px] gap-1"
                    onClick={handleReset}
                  >
                    <RotateCcw className="h-3 w-3" />
                    Reset
                  </Button>
                </div>

                <DiffPane
                  original={job.original_text}
                  rewritten={job.rewritten_text}
                />

                {/* Apply / discard */}
                {!applied ? (
                  <div className="flex items-center gap-3 sticky bottom-0 bg-background/95 py-3 border-t border-border -mx-5 px-5">
                    <Button
                      className="flex-1 gap-2"
                      disabled={applying}
                      onClick={handleApply}
                      data-testid="ht-apply-btn"
                    >
                      {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                      {applying ? 'Applying…' : 'Apply Rewrite to Chapter'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleReset}
                      data-testid="ht-discard-btn"
                    >
                      Discard
                    </Button>
                  </div>
                ) : (
                  <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-[14px] text-emerald-700 flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 shrink-0" />
                    Rewrite applied successfully to this chapter.
                  </div>
                )}
              </>
            )}

          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
